import React, { useRef, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import { createConsultantApi } from "../../../services/ConsultantService";

const GENDER_OPTIONS = [
  { value: "", label: "Select Gender" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const PROFESSION_OPTIONS = [
  { value: "", label: "Select Profession" },
  { value: "Psychologist", label: "Psychologist" },
  { value: "Psychiatrist", label: "Psychiatrist" },
  { value: "Counselor", label: "Counselor" },
  { value: "Life Coach", label: "Life Coach" },
  { value: "Mental Wellness Coach", label: "Mental Wellness Coach" },
  { value: "Social Worker", label: "Social Worker" },
  { value: "Yoga Coach", label: "Yoga Coach" },
  { value: "Dietician", label: "Dietician" },
  { value: "Nutritionist", label: "Nutritionist" },
  { value: "Fitness Trainer", label: "Fitness Trainer" },
  { value: "Other", label: "Other" },
];

const ALLOWED_DOC_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AddConsultant = () => {
  const navigate = useNavigate();
  const profileInputRef = useRef(null);
  const docInputRef = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    gender: "",
    qualification: "",
    profession: "",
    experience: "",
    ratePerMinute: "",
    bio: "",
    dateOfBirth: "",
  });

  const [profileImage, setProfileImage] = useState(null);   // { file, preview }
  const [documents, setDocuments] = useState([]);            // [{ file, name }]
  const [errors, setErrors] = useState({});

  // ── Field change ─────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Profile image ─────────────────────────────────────────────────────────
  const handleProfileImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((p) => ({ ...p, profileImage: "Only image files are allowed." }));
      return;
    }
    const preview = URL.createObjectURL(file);
    setProfileImage({ file, preview });
    setErrors((p) => ({ ...p, profileImage: "" }));
  };

  // ── Documents ─────────────────────────────────────────────────────────────
  const handleDocuments = (e) => {
    const files = Array.from(e.target.files);
    const invalid = files.filter((f) => !ALLOWED_DOC_TYPES.includes(f.type));
    if (invalid.length) {
      setErrors((p) => ({
        ...p,
        documents: "Only PDF, DOC, and DOCX files are allowed.",
      }));
      return;
    }
    setDocuments((prev) => [
      ...prev,
      ...files.map((f) => ({ file: f, name: f.name })),
    ]);
    setErrors((p) => ({ ...p, documents: "" }));
    e.target.value = "";
  };

  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email.";
    if (!form.password) e.password = "Password is required.";
    else if (form.password.length < 6)
      e.password = "Password must be at least 6 characters.";
    if (!form.confirmPassword) e.confirmPassword = "Please confirm your password.";
    else if (form.password !== form.confirmPassword)
      e.confirmPassword = "Passwords do not match.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    if (!form.ratePerMinute || isNaN(Number(form.ratePerMinute)) || Number(form.ratePerMinute) <= 0)
      e.ratePerMinute = "Enter a valid rate per minute.";
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({ icon: "error", title: "Validation Error", text: "Please fill in all required fields correctly." });
      return;
    }

    try {
      setLoading(true);

      // Convert profile image to base64
      let profileImagePayload = undefined;
      if (profileImage?.file) {
        const data = await fileToBase64(profileImage.file);
        profileImagePayload = {
          data,
          mimeType: profileImage.file.type,
          name: profileImage.file.name,
        };
      }

      // Convert documents to base64
      const documentPayloads = await Promise.all(
        documents.map(async (d) => ({
          data: await fileToBase64(d.file),
          mimeType: d.file.type,
          name: d.name,
        }))
      );

      await createConsultantApi({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim() || undefined,
        gender: form.gender || undefined,
        qualification: form.qualification.trim() || undefined,
        profession: form.profession || undefined,
        experience: form.experience.trim() || undefined,
        ratePerMinute: Number(form.ratePerMinute),
        bio: form.bio.trim() || undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        profileImage: profileImagePayload,
        documents: documentPayloads.length ? documentPayloads : undefined,
      });

      Swal.fire({
        icon: "success",
        title: "Consultant Created!",
        text: `${form.name} has been added successfully.`,
        confirmButtonText: "Go to Consultants",
      }).then(() => navigate("/all-consultant"));
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create consultant. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/all-consultant");

  return (
    <>
      <PageTitle activeMenu="Add Consultant" motherMenu="Consultants" />
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Consultant Information</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">

                  {/* ── Name ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        name="name"
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="Enter full name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                  </div>

                  {/* ── Email ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="email@example.com"
                        value={form.email}
                        onChange={handleChange}
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                  </div>

                  {/* ── Password ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group pass-group">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          className={`form-control pass-input ${errors.password ? "is-invalid" : ""}`}
                          placeholder="Min. 6 characters"
                          value={form.password}
                          onChange={handleChange}
                        />
                        <span
                          className={`input-group-text pass-handle ${showPassword ? "active" : ""}`}
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </span>
                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                      </div>
                    </div>
                  </div>

                  {/* ── Confirm Password ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group pass-group">
                        <input
                          name="confirmPassword"
                          type={showConfirm ? "text" : "password"}
                          className={`form-control pass-input ${errors.confirmPassword ? "is-invalid" : ""}`}
                          placeholder="Re-enter password"
                          value={form.confirmPassword}
                          onChange={handleChange}
                        />
                        <span
                          className={`input-group-text pass-handle ${showConfirm ? "active" : ""}`}
                          onClick={() => setShowConfirm(!showConfirm)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </span>
                        {errors.confirmPassword && <div className="invalid-feedback d-block">{errors.confirmPassword}</div>}
                      </div>
                    </div>
                  </div>

                  {/* ── Phone ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        value={form.phone}
                        onChange={handleChange}
                      />
                      {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>
                  </div>

                  {/* ── Gender ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Gender</label>
                      <Select
                        isSearchable={false}
                        options={GENDER_OPTIONS}
                        value={GENDER_OPTIONS.find((o) => o.value === form.gender) || GENDER_OPTIONS[0]}
                        onChange={(opt) =>
                          setForm((p) => ({ ...p, gender: opt.value }))
                        }
                        className="custom-react-select"
                      />
                    </div>
                  </div>

                  {/* ── Date of Birth ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Date of Birth</label>
                      <DatePicker
                        selected={form.dateOfBirth ? new Date(form.dateOfBirth) : null}
                        onChange={(date) =>
                          setForm((p) => ({
                            ...p,
                            dateOfBirth: date ? date.toISOString().split("T")[0] : "",
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText="DD/MM/YYYY"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        maxDate={new Date()}
                        className="form-control"
                        wrapperClassName="w-100"
                      />
                    </div>
                  </div>

                  {/* ── Rate per minute ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Rate per Minute (₹) <span className="text-danger">*</span>
                      </label>
                      <input
                        name="ratePerMinute"
                        type="number"
                        min="1"
                        className={`form-control ${errors.ratePerMinute ? "is-invalid" : ""}`}
                        placeholder="e.g. 10"
                        value={form.ratePerMinute}
                        onChange={handleChange}
                      />
                      {errors.ratePerMinute && <div className="invalid-feedback">{errors.ratePerMinute}</div>}
                    </div>
                  </div>

                  {/* ── Profession ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Profession</label>
                      <Select
                        isSearchable={false}
                        options={PROFESSION_OPTIONS}
                        value={PROFESSION_OPTIONS.find((o) => o.value === form.profession) || PROFESSION_OPTIONS[0]}
                        onChange={(opt) =>
                          setForm((p) => ({ ...p, profession: opt.value }))
                        }
                        className="custom-react-select"
                      />
                    </div>
                  </div>

                  {/* ── Qualification ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Qualification</label>
                      <input
                        name="qualification"
                        type="text"
                        className="form-control"
                        placeholder="e.g. M.Sc. Psychology"
                        value={form.qualification}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* ── Experience ── */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Experience</label>
                      <input
                        name="experience"
                        type="text"
                        className="form-control"
                        placeholder="e.g. 5 years"
                        value={form.experience}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* ── Bio ── */}
                  <div className="col-sm-12">
                    <div className="form-group">
                      <label className="form-label">Bio</label>
                      <textarea
                        name="bio"
                        rows={3}
                        className="form-control"
                        placeholder="Short bio about the consultant"
                        value={form.bio}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* ── Profile Image ── */}
                  <div className="col-lg-6">
                    <div className="form-group">
                      <label className="form-label">Profile Picture</label>
                      <input
                        ref={profileInputRef}
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={handleProfileImage}
                      />
                      {errors.profileImage && (
                        <div className="text-danger fs-12 mt-1">{errors.profileImage}</div>
                      )}
                      {profileImage?.preview && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img
                            src={profileImage.preview}
                            alt="Preview"
                            style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }}
                          />
                          <button
                            type="button"
                            className="btn btn-xs btn-danger"
                            onClick={() => {
                              setProfileImage(null);
                              if (profileInputRef.current) profileInputRef.current.value = "";
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Qualification Documents ── */}
                  <div className="col-lg-6">
                    <div className="form-group">
                      <label className="form-label">
                        Qualification Documents{" "}
                        <small className="text-muted">(PDF, DOC, DOCX — multiple allowed)</small>
                      </label>
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="form-control"
                        multiple
                        onChange={handleDocuments}
                      />
                      {errors.documents && (
                        <div className="text-danger fs-12 mt-1">{errors.documents}</div>
                      )}
                      {documents.length > 0 && (
                        <ul className="list-group mt-2">
                          {documents.map((doc, i) => (
                            <li
                              key={i}
                              className="list-group-item d-flex justify-content-between align-items-center py-1 px-2"
                              style={{ fontSize: 13 }}
                            >
                              <span>
                                <i className="fa fa-file-pdf-o me-1 text-danger" />
                                {doc.name}
                              </span>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-danger"
                                onClick={() => removeDocument(i)}
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="col-12 mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Creating...
                        </>
                      ) : (
                        "Create Consultant"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddConsultant;
